// frontend/src/contexts/ProfileContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { profileService } from '../components/services/profileServices';

const ProfileContext = createContext();

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error('useProfile must be used within ProfileProvider');
    }
    return context;
};

export const ProfileProvider = ({ children }) => {
    const [profile, setProfile] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Carica profilo
    const loadProfile = useCallback(async () => {
        setLoading(true);
        try {
            const response = await profileService.getProfile();
            console.log('📸 Profile response:', response);
            
            if (response && response.success) {
                setProfile(response.user);  
                setAddresses(response.user?.indirizzi || []);
                setError(null);
                console.log('📸 Avatar URL:', response.user?.avatar_url);
            } else {
                setError(response?.message || 'Errore caricamento profilo');
            }
        } catch (err) {
            console.error('Load profile error:', err);
            setError(err.response?.data?.message || 'Errore caricamento profilo');
        } finally {
            setLoading(false);
        }
    }, []);

    // Aggiorna profilo
    const updateProfile = async (profileData) => {
        setLoading(true);
        try {
            const response = await profileService.updateProfile(profileData);
            if (response && response.success) {
                setProfile(prev => ({ ...prev, ...response.data }));
                return { success: true, data: response.data };
            } else {
                return { success: false, error: response?.message };
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Errore aggiornamento profilo';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    };

    // Cambio password
    const changePassword = async (passwordData) => {
        setLoading(true);
        try {
            const response = await profileService.changePassword(passwordData);
            if (response && response.success) {
                return { success: true, message: response.message };
            } else {
                return { success: false, error: response?.message };
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Errore cambio password';
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    };

    // Carica ordini (opzionale - non bloccare se fallisce)
    const loadOrders = useCallback(async (page = 1) => {
        try {
            const response = await profileService.getUserOrders(page);
            if (response && response.success) {
                setOrders(response.data?.orders || []);
            } else {
                // Non mostrare errore per ordini, solo log
                console.log('Ordini non disponibili:', response?.message);
                setOrders([]);
            }
        } catch (err) {
            console.log('Ordini non ancora implementati');
            setOrders([]);
        }
    }, []);

    // Refresh completo
    const refreshProfile = useCallback(async () => {
        await loadProfile();
        await loadOrders();
    }, [loadProfile, loadOrders]);

    // Carica indirizzi separatamente
    const loadAddresses = useCallback(async () => {
        try {
            const response = await profileService.getAddresses();
            if (response && response.success) {
                setAddresses(response.addresses || []);
            }
        } catch (err) {
            console.error('Load addresses error:', err);
            setAddresses([]);
        }
    }, []);

    // Carica iniziale
    useEffect(() => {
        loadProfile();
        loadOrders();
    }, [loadProfile, loadOrders]);

    const value = {
        profile,
        addresses,
        orders,
        loading,
        error,
        updateProfile,
        changePassword,
        loadProfile,
        loadOrders,
        loadAddresses,
        refreshProfile,
        setAddresses
    };

    return (
        <ProfileContext.Provider value={value}>
            {children}
        </ProfileContext.Provider>
    );
};
